import { CommonHelperFunctions, IWorkFlowDto, IWorkFlowVersionDto } from "@kuika/kuika-cl-model"; //
import {
  IProjectsWithExternalWFResponseDto,
  IProjectsWithWFResponseDto
} from "@kuika/kuika-cl-model/src/dtos/projects-response";
import { IProcessTemplateDto, IUpdateWFVersionDto, IWFVersionDto } from "@kuika/kuika-cl-model/src/dtos/workflow.dto";
import Axios, { AxiosResponse } from "axios";
import { KMainFunctions } from "../shared/utility/main-functions";

const backendUrl = CommonHelperFunctions.GetBackendUrl();

/** Response of the lightweight `workflow/lastmodified/{id}` change-token endpoint. */
interface IWorkflowLastModifiedResponse {
  updatedAt: string | null;
}

export class WorkFlowService {
  public static async Create(screenDesignDocument: IWorkFlowDto): Promise<AxiosResponse> {
    try {
      return await Axios.post<IWorkFlowDto, AxiosResponse>(`${backendUrl}/workflow/create`, screenDesignDocument);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /** The process-template catalog (backend Metadatas/process-templates.json, served verbatim). */
  public static async GetProcessTemplates(): Promise<AxiosResponse<IProcessTemplateDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse<IProcessTemplateDto[]>>(`${backendUrl}/workflow/templates`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Update(id: string, obj: IWorkFlowDto): Promise<AxiosResponse> {
    try {
      var result = await Axios.put<IWorkFlowDto, AxiosResponse>(`${backendUrl}/workflow/update/${id}`, obj);
      return result;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Delete(id: string): Promise<boolean> {
    try {
      await Axios.delete<null>(`${backendUrl}/workflow/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async Duplicate(obj: any): Promise<AxiosResponse> {
    try {
      var result = await Axios.post<null>(`${backendUrl}/workflow/duplicate/`, obj);
      return result;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Inactivate(id: string): Promise<boolean> {
    try {
      await Axios.post<null>(`${backendUrl}/workflow/inactive/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async Activate(id: string): Promise<boolean> {
    try {
      await Axios.post<null>(`${backendUrl}/workflow/activate/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async Get(id: string): Promise<AxiosResponse<IWorkFlowDto>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Lightweight change-token — the process's audit UpdatedDate only, no Xml/Json. Lets the designer
   * decide whether a cached process is still current before paying for a full {@link Get}. Returns
   * undefined on any failure so callers safely fall back to a full fetch.
   */
  public static async GetLastModified(id: string): Promise<string | undefined> {
    try {
      const res: AxiosResponse<IWorkflowLastModifiedResponse> = await Axios.get<
        null,
        AxiosResponse<IWorkflowLastModifiedResponse>
      >(`${backendUrl}/workflow/lastmodified/${id}`);
      return res?.data?.updatedAt ?? undefined;
    } catch {
      return undefined;
    }
  }

  public static async GetAllByProjectId(id: string): Promise<AxiosResponse<IWorkFlowDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/getallbyprojectid/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAll(): Promise<AxiosResponse<IWorkFlowDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/getall`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectList(): Promise<AxiosResponse<IProjectsWithWFResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/listprojectswithworkflows`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectsWithExternalWFScreen(): Promise<AxiosResponse<IProjectsWithExternalWFResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/listprojectswithexternalworkflows`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async CheckWorkflowInMultipleProjects(id: string): Promise<boolean> {
    try {
      await Axios.get<null>(`${backendUrl}/workflow/checkworkflowsbydomain/${id}`);
      return true;
    } catch (error: any) {
      // KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async GenerateVersion(id: string, obj: IWorkFlowDto): Promise<AxiosResponse> {
    try {
      var result = await Axios.post<IWorkFlowDto, AxiosResponse>(`${backendUrl}/workflow/generateversion/${id}`, obj);
      return result;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetVersion(id: string): Promise<AxiosResponse<IWorkFlowVersionDto>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/version/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetVersions(id: string): Promise<AxiosResponse<IWFVersionDto[]>> {
    try {
      return await Axios.get<null, AxiosResponse>(`${backendUrl}/workflow/getVersions/${id}`);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async ActivateVersion(id: string): Promise<boolean> {
    try {
      await Axios.put<null>(`${backendUrl}/workflow/activateVersion/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async DeactivateVersion(id: string): Promise<boolean> {
    try {
      await Axios.put<null>(`${backendUrl}/workflow/deactivateVersion/${id}`);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async UpdateVersion(updateDto: IUpdateWFVersionDto): Promise<boolean> {
    try {
      await Axios.put<null>(`${backendUrl}/workflow/updateVersion/`, updateDto);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  public static async UpdateLock(workflowId?: string) {
    try {
      const url = workflowId ? `${backendUrl}/workflow/updateLock/${workflowId}` : `${backendUrl}/workflow/updateLock`;
      await Axios.post<null>(url);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }
}
